// src/components/common/FileViewer.js
//
// The per-kind file viewer, extracted from the agent's FilePane so the same
// rendering rules serve every surface that shows a stored file. It owns the
// content area only — fetching the bytes, the kind detection, and the
// download — while the host owns its own chrome (header, rail, layout). The
// fetch flow is FilePane's: bytes from a URL, a 401/403 re-sign from gcsPath
// and one retry, and an AbortController on file switch. Only .xlsx
// participates in edit tracking.
//
// A file may arrive with a signed `url` already attached (the agent's canvas
// files always do) or with only a `gcsPath` (a file explorer listing rows from
// Firestore). A supplied `url` is used synchronously so nothing gains a
// signing round-trip; a gcsPath-only file is signed on demand.
//
// A host that shows the same files over and over — the run result's tab strip,
// which mounts one tab at a time — may hand in `bytesCache`, a Map from storage
// path to fetched bytes that the host owns and bounds. A file whose bytes are
// already in it is shown without a fetch, and a fetch that succeeds fills it,
// within a budget: a result's folder can hold dozens of workbooks, and opening
// them one after another must not keep every one in memory. Only the kinds that
// fetch bytes take part (xlsx, docx, image): pdf embeds a signed URL and text has
// its own capped fetch.
import ViewerErrorBoundary from './ViewerErrorBoundary';
import React, { lazy, useState, useEffect, useRef, forwardRef, useImperativeHandle, Suspense } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { getSignedUrl, fetchGcsText } from '../../services/api_utils';
import { exportWorkbookBlob, downloadBlob } from '../../utils/canvasWorkbook';

const SpreadsheetViewer = lazy(() => import('./SpreadsheetViewer'));
const DocumentViewer = lazy(() => import('../assistant/DocumentViewer'));

const TEXT_PREVIEW_CAP = 2 * 1024 * 1024; // 2 MB
const BYTE_KINDS = new Set(['xlsx', 'docx', 'image']);
const BYTES_CACHE_BUDGET = 64 * 1024 * 1024; // 64 MB

// Stores the bytes under the key, dropping the oldest entries (a Map iterates in
// insertion order) until the whole cache fits the budget. Bytes that would not
// fit on their own are not stored at all.
export const cacheBytes = (bytesCache, key, buffer, budget = BYTES_CACHE_BUDGET) => {
  if (buffer.byteLength > budget) return;
  let held = 0;
  bytesCache.forEach((bytes) => { held += bytes.byteLength; });
  for (const [oldestKey, bytes] of bytesCache) {
    if (held + buffer.byteLength <= budget) break;
    bytesCache.delete(oldestKey);
    held -= bytes.byteLength;
  }
  bytesCache.set(key, buffer);
};

const extOf = (filename) => '.' + (filename || '').split('.').pop().toLowerCase();

export const viewerKindFor = (filename) => {
  const ext = extOf(filename);
  if (ext === '.xlsx') return 'xlsx';
  if (ext === '.docx') return 'docx';
  if (['.png', '.jpg', '.jpeg', '.gif', '.webp'].includes(ext)) return 'image';
  if (ext === '.pdf') return 'pdf';
  if (['.csv', '.json', '.txt', '.md', '.log'].includes(ext)) return 'text';
  return 'other';
};

// A caller-supplied url is returned as-is — awaiting it costs a microtask and
// no network call. Only a file that carries nothing but a gcsPath is signed.
const resolveUrl = async (file) => file.url || getSignedUrl(file.gcsPath);

const FileViewer = forwardRef(function FileViewer({
  file,               // { url?, gcsPath?, filename, contentType? }
  editable = false,
  onOp,
  onLoad,
  workbookRef,
  // Download exports the live workbook instead of the fetched bytes: the host sets
  // this whenever the editor has diverged from them — unsent edits, or edits already
  // sent while the pane still shows the edited workbook.
  exportLive = false,
  textWrap = true,
  onError,
  // Map from storage path to fetched bytes, owned by the host (see the header).
  bytesCache = null,
  sx,
}, ref) {
  const [fileBytes, setFileBytes] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [textTruncated, setTextTruncated] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // A failed download copy, kept apart from `error` (a preview that could not
  // be produced): the preview already on screen stays exactly as it is.
  const [downloadError, setDownloadError] = useState(null);
  const abortRef = useRef(null);

  const kind = viewerKindFor(file?.filename);

  // Errors keep rendering in the content area — that is the host-agnostic
  // place for them — and are additionally pushed to a caller that wants to
  // react in its own chrome.
  const reportError = (message) => {
    setError(message);
    onError?.(message);
  };

  useEffect(() => {
    setFileBytes(null);
    setTextContent(null);
    setTextTruncated(false);
    setSignedUrl(null);
    setDownloadError(null);
    reportError(null);

    if (!file?.url && !file?.gcsPath) {
      if (abortRef.current) abortRef.current.abort();
      setLoading(false);
      return undefined;
    }

    // pdf embeds a URL directly and 'other' needs no content at all, so
    // neither fetches bytes. When the file already carries a url there is
    // nothing to wait for — the <object> must never flash a spinner.
    if ((kind === 'pdf' || kind === 'other') && file.url) {
      if (abortRef.current) abortRef.current.abort();
      setLoading(false);
      return undefined;
    }

    // Bytes the host already holds for this file are shown as they are: no
    // fetch, and a fetch still running for the previous file is dropped so it
    // cannot land over them.
    const cacheKey = file.gcsPath || file.url;
    const cachedBytes = bytesCache && BYTE_KINDS.has(kind) ? bytesCache.get(cacheKey) : null;
    if (cachedBytes) {
      if (abortRef.current) abortRef.current.abort();
      setFileBytes(cachedBytes);
      setLoading(false);
      return undefined;
    }

    // Abort the previous fetch if still in-flight (file switch).
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    const fetchContent = async () => {
      if (kind === 'pdf' || kind === 'other') {
        // Only reachable for a gcsPath-only file: sign it so the <object> (and
        // a subsequent download) has somewhere to point.
        const url = await getSignedUrl(file.gcsPath);
        if (controller.signal.aborted) return;
        setSignedUrl(url);
        setLoading(false);
        return;
      }

      if (kind === 'text') {
        // Text preview via fetchGcsText (§2.2); URL-only files (live
        // file_available cards carry no gcs_path) fall back to a direct fetch.
        let text;
        if (file.gcsPath) {
          text = await fetchGcsText(file.gcsPath);
        } else {
          const response = await fetch(file.url, { signal: controller.signal });
          if (!response.ok) throw new Error(`Failed to load file (${response.status})`);
          text = await response.text();
        }
        if (controller.signal.aborted) return;
        if (text.length > TEXT_PREVIEW_CAP) {
          setTextContent(text.slice(0, TEXT_PREVIEW_CAP));
          setTextTruncated(true);
        } else {
          setTextContent(text);
        }
        setLoading(false);
        return;
      }

      let response = await fetch(await resolveUrl(file), { signal: controller.signal });

      // Stale signed URL — re-sign and retry once (v1 flow).
      if ((response.status === 401 || response.status === 403) && file.gcsPath) {
        const freshUrl = await getSignedUrl(file.gcsPath);
        response = await fetch(freshUrl, { signal: controller.signal });
      }

      if (!response.ok) throw new Error(`Failed to load file (${response.status})`);

      const buffer = await response.arrayBuffer();
      if (!controller.signal.aborted) {
        // Only the byte kinds reach this point; text, pdf and other returned above.
        if (bytesCache) cacheBytes(bytesCache, cacheKey, buffer);
        setFileBytes(buffer);
        setLoading(false);
      }
    };

    fetchContent().catch((err) => {
      if (err.name === 'AbortError' || controller.signal.aborted) return;
      reportError(err.message);
      setLoading(false);
    });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.url, file?.gcsPath, kind]);

  // Image bytes → object URL (revoked on change).
  useEffect(() => {
    if (kind !== 'image' || !fileBytes) {
      setImageUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(new Blob([fileBytes], { type: file?.contentType || 'image/*' }));
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileBytes, kind]);

  const handleDownload = async () => {
    if (!file) return;
    setDownloadError(null);
    try {
      // Export the live editor state whenever it has diverged from the bytes.
      if (kind === 'xlsx' && exportLive && workbookRef?.current) {
        const blob = await exportWorkbookBlob(workbookRef);
        downloadBlob(blob, file.filename || 'canvas_edit.xlsx');
        return;
      }
      if (fileBytes) {
        downloadBlob(new Blob([fileBytes], { type: file.contentType }), file.filename || 'download');
        return;
      }
      // pdf / other / text-without-bytes: fetch fresh, re-signing once if
      // stale. A url signed for the pdf <object> is reused rather than signed
      // a second time.
      let response = await fetch(signedUrl || await resolveUrl(file));
      if ((response.status === 401 || response.status === 403) && file.gcsPath) {
        const freshUrl = await getSignedUrl(file.gcsPath);
        response = await fetch(freshUrl);
      }
      if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
      downloadBlob(await response.blob(), file.filename || 'download');
    } catch (err) {
      console.error('Failed to download file:', err);
      setDownloadError(err.message || 'Download failed.');
    }
  };

  // The host's own download control drives the same path as the download card,
  // so the workbook-export and re-sign rules live in one place.
  useImperativeHandle(ref, () => ({ download: handleDownload }));

  if (!file) return null;

  const downloadCard = (message) => (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={1.5} p={3}>
      <InsertDriveFileIcon sx={{ fontSize: 44, color: 'text.secondary' }} />
      <Typography variant="subtitle2" sx={{ wordBreak: 'break-word', textAlign: 'center' }}>{file.filename}</Typography>
      {message && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>{message}</Typography>
      )}
      <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ textTransform: 'none' }}>
        Download
      </Button>
    </Box>
  );

  const renderViewer = () => {
    if (loading) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading file...
          </Typography>
        </Box>
      );
    }

    // Whatever failed — the fetch or the viewer's own read of the bytes — the file
    // itself is still offered for download.
    if (error) return downloadCard(error);

    if (kind === 'pdf') {
      return (
        <Box
          component="object"
          data={file.url || signedUrl}
          type="application/pdf"
          sx={{ flex: 1, width: '100%', height: '100%' }}
        >
          {/* Rendered when the browser can't load the object (e.g. an expired
              signed URL) — a visible degrade, never a silent blank pane. */}
          {downloadCard('The PDF preview could not be displayed.')}
        </Box>
      );
    }

    if (kind === 'other') {
      return downloadCard(null);
    }

    if (kind === 'text') {
      if (textContent == null) return null;
      return (
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {textTruncated && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pt: 1 }}>
              Preview truncated at 2 MB — download the file for the full content.
            </Typography>
          )}
          <Box
            component="pre"
            sx={{
              m: 0,
              px: 1.5,
              py: 1,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              whiteSpace: textWrap ? 'pre-wrap' : 'pre',
              wordBreak: textWrap ? 'break-word' : 'normal',
              overflow: 'auto',
              flex: 1,
            }}
          >
            {textContent}
          </Box>
        </Box>
      );
    }

    if (kind === 'image') {
      if (!imageUrl) return null;
      return (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', p: 1 }}>
          <Box
            component="img"
            src={imageUrl}
            alt={file.filename}
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </Box>
      );
    }

    if (!fileBytes) return null;

    return (
      <ViewerErrorBoundary key={file.gcsPath || file.url || file.filename} onDownload={handleDownload}>
        <Suspense fallback={
          <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
            <CircularProgress size={24} />
          </Box>
        }>
          {kind === 'xlsx' && (
            <SpreadsheetViewer
              fileBytes={fileBytes}
              editable={editable}
              onOp={onOp}
              onLoad={onLoad}
              onError={reportError}
              ref={workbookRef}
            />
          )}
          {kind === 'docx' && (
            <DocumentViewer fileBytes={fileBytes} onError={reportError} />
          )}
        </Suspense>
      </ViewerErrorBoundary>
    );
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', ...sx }}>
      {/* The preview keeps the remaining height whatever its own sizing
          (the xlsx/docx viewers use height: 100%), so the bar below is never
          pushed out of the clipped column. */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {renderViewer()}
      </Box>
      {downloadError && (
        <Box sx={{ px: 1.5, py: 0.75, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="body2" color="error" sx={{ flex: 1, minWidth: 0 }}>
            {downloadError} — try again.
          </Typography>
          <Button size="small" onClick={() => setDownloadError(null)} sx={{ textTransform: 'none', flexShrink: 0 }}>
            Dismiss
          </Button>
        </Box>
      )}
    </Box>
  );
});

export default FileViewer;
